import {
  LicenseResponseSchema,
  LicenseTicketPayloadSchema,
  type LicenseResponse,
  type LicenseStatus,
  type LicenseTicketPayload,
} from "@legacy-manager/shared";

import type { ClienteDaApi } from "./cliente";
import { cofre } from "./cofre";

/**
 * A licença do jogo, do lado do aparelho.
 *
 * O jogo é comprado, com teste grátis de sete dias, e roda sem internet. O
 * servidor entrega um **bilhete assinado** dizendo até quando este aparelho
 * pode jogar (`POST /v1/license`), e aqui ele é conferido com a chave pública
 * que vem dentro do build. Um bilhete editado à mão não confere; um bilhete de
 * outro aparelho também não.
 *
 * O relógio do aparelho entra em uma conta só: se ele andar para trás do maior
 * horário já visto (alguém atrasou a hora para esticar o bilhete), o bilhete
 * guardado deixa de valer até o jogo falar com o servidor de novo.
 */

const CHAVE_PUBLICA = import.meta.env.VITE_LICENSE_PUBLIC_KEY?.trim() ?? "";
const GUARDADA = "lm.licenca";
const RELOGIO = "lm.licenca.relogio";
/** Folga para relógio que se acerta sozinho pela rede, como fuso e horário de verão. */
const FOLGA_DO_RELOGIO_MS = 2 * 60 * 60 * 1000;

/**
 * Build de desenvolvimento sem chave pública joga sem licença. Build de
 * produção sem chave não libera nada: sem chave não há o que conferir, e
 * liberar seria entregar o jogo.
 */
export const LICENCA_DESLIGADA = import.meta.env.DEV && CHAVE_PUBLICA === "";

export interface LicencaGuardada {
  status: LicenseStatus;
  trialEndsAt: string | null;
  ticket: string | null;
}

// ---------------------------------------------------------------------------
// O aparelho
// ---------------------------------------------------------------------------

let aparelho: Promise<string> | null = null;

function sortearId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return btoa(String.fromCharCode(...bytes)).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

/** O identificador deste aparelho, sorteado uma vez e guardado no cofre. */
export function idDoAparelho(): Promise<string> {
  aparelho ??= (async () => {
    const guardado = await cofre()
      .ler("aparelho")
      .catch(() => null);
    if (guardado && /^[A-Za-z0-9_-]{16,64}$/.test(guardado)) return guardado;
    const novo = sortearId();
    await cofre()
      .gravar("aparelho", novo)
      .catch(() => undefined);
    return novo;
  })();
  return aparelho;
}

// ---------------------------------------------------------------------------
// O bilhete
// ---------------------------------------------------------------------------

let chave: Promise<CryptoKey | null> | null = null;

function deBase64(texto: string): Uint8Array<ArrayBuffer> {
  const normal = texto.replaceAll("-", "+").replaceAll("_", "/");
  const binario = atob(normal + "=".repeat((4 - (normal.length % 4)) % 4));
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

function chavePublica(): Promise<CryptoKey | null> {
  chave ??= CHAVE_PUBLICA
    ? crypto.subtle
        .importKey("spki", deBase64(CHAVE_PUBLICA), { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"])
        .catch(() => null)
    : Promise.resolve(null);
  return chave;
}

/**
 * Confere a assinatura e abre o bilhete. `null` para qualquer coisa que não
 * seja um bilhete do nosso servidor para este aparelho.
 */
export async function abrirBilhete(bilhete: string, dispositivo: string): Promise<LicenseTicketPayload | null> {
  const [corpo, assinatura, sobra] = bilhete.split(".");
  if (!corpo || !assinatura || sobra !== undefined) return null;
  const publica = await chavePublica();
  if (!publica) return null;
  try {
    const confere = await crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      publica,
      deBase64(assinatura),
      new TextEncoder().encode(corpo),
    );
    if (!confere) return null;
    const dados = LicenseTicketPayloadSchema.safeParse(JSON.parse(new TextDecoder().decode(deBase64(corpo))));
    if (!dados.success || dados.data.dev !== dispositivo) return null;
    return dados.data;
  } catch {
    return null;
  }
}

/** O relógio andou para trás do maior horário já visto? Guarda o novo maior. */
function relogioRecuou(agora: number): boolean {
  try {
    const maior = Number(window.localStorage.getItem(RELOGIO) ?? "0");
    if (Number.isFinite(maior) && agora + FOLGA_DO_RELOGIO_MS < maior) return true;
    if (agora > maior) window.localStorage.setItem(RELOGIO, String(agora));
  } catch {
    // Sem armazenamento não há como comparar; a validade do bilhete continua valendo.
  }
  return false;
}

function lerGuardada(): LicencaGuardada | null {
  try {
    const texto = window.localStorage.getItem(GUARDADA);
    return texto ? (JSON.parse(texto) as LicencaGuardada) : null;
  } catch {
    return null;
  }
}

function guardar(licenca: LicencaGuardada | null): void {
  try {
    if (licenca) window.localStorage.setItem(GUARDADA, JSON.stringify(licenca));
    else window.localStorage.removeItem(GUARDADA);
  } catch {
    // Sem armazenamento, o jogo pede a licença ao servidor a cada abertura.
  }
}

// ---------------------------------------------------------------------------
// O que as telas usam
// ---------------------------------------------------------------------------

export interface Liberacao {
  /** Pode jogar agora, neste aparelho. */
  liberado: boolean;
  /** O que o servidor disse por último; nulo se nunca disse nada aqui. */
  status: LicenseStatus | null;
  trialEndsAt: string | null;
  /** Até quando o bilhete vale sem internet (milissegundos). */
  validoAte: number | null;
}

/** O que dá para saber sem rede: o bilhete guardado, conferido. */
export async function liberacaoLocal(agora = Date.now()): Promise<Liberacao> {
  if (LICENCA_DESLIGADA) return { liberado: true, status: "licensed", trialEndsAt: null, validoAte: null };
  const guardada = lerGuardada();
  const recuou = relogioRecuou(agora);
  if (!guardada) return { liberado: false, status: null, trialEndsAt: null, validoAte: null };
  const bilhete = guardada.ticket ? await abrirBilhete(guardada.ticket, await idDoAparelho()) : null;
  const validoAte = bilhete ? bilhete.exp * 1000 : null;
  return {
    liberado: !recuou && validoAte !== null && validoAte > agora,
    status: guardada.status,
    trialEndsAt: guardada.trialEndsAt,
    validoAte,
  };
}

/**
 * Pergunta ao servidor, guarda o bilhete e devolve a liberação conferida.
 *
 * `iniciarTeste` começa os sete dias se eles ainda não começaram; o jogo manda
 * ao abrir uma carreira, nunca ao só mostrar a tela inicial.
 */
export async function consultarLicenca(
  cliente: ClienteDaApi,
  opcoes: { iniciarTeste?: boolean } = {},
): Promise<{ resposta: LicenseResponse; liberacao: Liberacao }> {
  const deviceId = await idDoAparelho();
  const corpo = opcoes.iniciarTeste ? { deviceId, startTrial: true } : { deviceId };
  const resposta = await cliente.pedir({ metodo: "POST", caminho: "/v1/license", corpo }, LicenseResponseSchema);
  guardar({ status: resposta.status, trialEndsAt: resposta.trialEndsAt, ticket: resposta.ticket });
  return { resposta, liberacao: await liberacaoLocal() };
}

/** Ao sair da conta, a licença deste aparelho sai junto. */
export function esquecerLicenca(): void {
  guardar(null);
}
